import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  GoogleLogo, MicrosoftOutlookLogo, WhatsappLogo, CreditCard, ShoppingBag, LinkedinLogo,
  CalendarBlank, Buildings,
} from "@phosphor-icons/react";

const INTEGRATIONS = [
  { name: "Gmail", icon: GoogleLogo, status: "Beschikbaar", desc: "E-mail synchronisatie en AI-voorstellen voor antwoorden." },
  { name: "Outlook", icon: MicrosoftOutlookLogo, status: "Beschikbaar", provider: "microsoft", desc: "E-mail verzenden en opvolgingen vastleggen." },
  { name: "WhatsApp Business", icon: WhatsappLogo, status: "Binnenkort", desc: "WhatsApp gesprekken versturen en loggen." },
  { name: "Stripe", icon: CreditCard, status: "Beschikbaar", desc: "Kaartbetalingen en abonnementsfacturatie." },
  { name: "Mollie", icon: CreditCard, status: "Binnenkort", desc: "Europese betaalmethoden." },
  { name: "Shopify", icon: ShoppingBag, status: "Binnenkort", desc: "Producten en orders synchroniseren." },
  { name: "LinkedIn", icon: LinkedinLogo, status: "Binnenkort", desc: "Leads en contacten verrijken." },
  { name: "Office 365 Agenda", icon: CalendarBlank, status: "Beschikbaar", provider: "microsoft", desc: "Tweeweg synchronisatie met je Microsoft 365-agenda." },
  { name: "Exact Online", icon: Buildings, status: "Binnenkort", desc: "Boekhouding en administratie." },
];

const ROLES = [
  ["super_admin", "Superbeheerder"],
  ["sales_manager", "Salesmanager"],
  ["finance", "Financieel"],
  ["employee", "Medewerker"],
  ["production_manager", "Productie"],
  ["investor_view", "Investeerder"],
  ["read_only", "Alleen lezen"],
];

export default function Settings() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [outlook, setOutlook] = useState({ connected: false, email: "" });
  const [outlookBusy, setOutlookBusy] = useState(false);
  const [outlookMessage, setOutlookMessage] = useState("");

  const loadOutlook = async () => {
    try {
      const { data } = await api.get("/integrations/outlook/status");
      setOutlook(data);
    } catch (error) {
      setOutlookMessage("Outlook-status kon niet worden geladen.");
    }
  };

  useEffect(() => {
    loadOutlook();
  }, []);

  const connectOutlook = async () => {
    setOutlookBusy(true);
    setOutlookMessage("");
    try {
      const { data } = await api.get("/integrations/outlook/connect");
      window.location.href = data.auth_url;
    } catch (error) {
      setOutlookMessage(error.response?.data?.detail || "Outlook koppelen is mislukt.");
      setOutlookBusy(false);
    }
  };

  const disconnectOutlook = async () => {
    setOutlookBusy(true);
    setOutlookMessage("");
    try {
      await api.post("/integrations/outlook/disconnect");
      setOutlook({ connected: false, email: "" });
      setOutlookMessage("Outlook is losgekoppeld.");
    } catch (error) {
      setOutlookMessage(error.response?.data?.detail || "Outlook loskoppelen is mislukt.");
    } finally {
      setOutlookBusy(false);
    }
  };

  const isMicrosoftIntegration = (integration) => integration.provider === "microsoft";

  return (
    <div className="space-y-8 max-w-5xl" data-testid="settings-page">
      <div>
        <div className="overline mb-2">- Werkruimte</div>
        <h1 className="font-display text-4xl tracking-tight leading-none">Instellingen</h1>
      </div>

      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-xl tracking-tight mb-4">Profiel</h2>
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Field label="Naam" value={user?.name} />
          <Field label="E-mail" value={user?.email} />
          <Field label="Rol" value={ROLES.find(([key]) => key === user?.role)?.[1] || user?.role} />
          <Field label="Aangemaakt" value={user?.created_at && new Date(user.created_at).toLocaleDateString("nl-NL")} />
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl tracking-tight">Weergave</h2>
        </div>
        <div className="flex items-center justify-between py-2">
          <div>
            <div className="text-sm">Donkere modus</div>
            <div className="text-xs text-muted-foreground">Standaard donker; schakel naar licht voor overdag.</div>
          </div>
          <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-theme-toggle" />
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-xl tracking-tight mb-4">Rollen en rechten</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {ROLES.map(([key, label]) => (
            <div key={key} className="rounded-lg border border-border bg-background px-3 py-2.5 text-sm">
              {label}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4">Fijnmazige rechten per module volgen in een volgende release.</p>
      </section>

      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-xl tracking-tight mb-1">Integraties</h2>
        <p className="text-sm text-muted-foreground mb-5">Koppel de tools die je team al gebruikt.</p>
        {outlookMessage && <div className="mb-4 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm">{outlookMessage}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {INTEGRATIONS.map((integration) => (
            <div key={integration.name} className="rounded-lg border border-border bg-background p-4 hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <integration.icon size={18} weight="duotone" className="text-primary" />
                <div className="text-sm font-medium">{integration.name}</div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3 min-h-[36px]">{integration.desc}</p>
              {isMicrosoftIntegration(integration) && outlook.connected && (
                <div className="mb-3 text-xs text-primary truncate">
                  {outlook.email || "Microsoft 365 gekoppeld"}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={integration.status === "Binnenkort"}
                onClick={isMicrosoftIntegration(integration) ? (outlook.connected ? disconnectOutlook : connectOutlook) : undefined}
                className="w-full text-xs rounded-full"
                data-testid={`integration-${integration.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {integration.status === "Binnenkort" ? "Binnenkort" : isMicrosoftIntegration(integration) && outlook.connected ? "Loskoppelen" : outlookBusy && isMicrosoftIntegration(integration) ? "Bezig..." : "Koppelen"}
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm capitalize">{value || "-"}</div>
    </div>
  );
}
